import { EntityType } from '@frontend/common';
import { ProductCategoryClient } from '@frontend/group/api';
import { ProductClient } from '@frontend/product/api';
import { CreateProduct, IProduct } from '@frontend/product/types';
import { updateField, useAccountRepository } from '@frontend/repository';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../redux/store';

const NAME_TRANSLATION_FIELDS = ['name_nl_be', 'name_en_us', 'name_fr_fr', 'name_de_de'] as const;
const DESCRIPTION_TRANSLATION_FIELDS = ['description_nl_be', 'description_en_us', 'description_fr_fr', 'description_de_de'] as const;
const USER_INFO_TRANSLATION_FIELDS = ['user_info_nl_be', 'user_info_en_us', 'user_info_fr_fr', 'user_info_de_de'] as const;
export const FORM_ID = 'create-product-modal';

export const useCreateProductModal = () => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const [values, setValues] = useState<string[]>(['']);

    const nameTranslationsModified = useRef<boolean>(false);
    const descriptionTranslationsModified = useRef<boolean>(false);
    const userInfoTranslationsModified = useRef<boolean>(false);

    const previousName = useRef<string>('');
    const previousDescription = useRef<string>('');
    const previousUserInfo = useRef<string>('');

    const form = useAppSelector((state) => state.forms);
    const formValues = useMemo(() => {
        const f = form[FORM_ID];
        if (!f) return {};
        return f.value;
    }, [form]);

    useEffect(() => {
        const currentName = (formValues.name as string) ?? '';
        const currentDescription = (formValues.description as string) ?? '';
        const currentUserInfo = (formValues.user_info as string) ?? '';

        if (!nameTranslationsModified.current) {
            const anyNameTranslationModified = NAME_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousName.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentName;
            });
            if (anyNameTranslationModified) {
                nameTranslationsModified.current = true;
            }
        }

        if (!descriptionTranslationsModified.current) {
            const anyDescriptionTranslationModified = DESCRIPTION_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousDescription.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentDescription;
            });
            if (anyDescriptionTranslationModified) {
                descriptionTranslationsModified.current = true;
            }
        }

        if (!userInfoTranslationsModified.current) {
            const anyUserInfoTranslationModified = USER_INFO_TRANSLATION_FIELDS.some((field) => {
                const fieldValue = (formValues[field] as string) ?? '';
                const expectedValue = previousUserInfo.current;
                return fieldValue !== '' && fieldValue !== expectedValue && fieldValue !== currentUserInfo;
            });
            if (anyUserInfoTranslationModified) {
                userInfoTranslationsModified.current = true;
            }
        }

        if (currentName !== previousName.current) {
            if (!nameTranslationsModified.current) {
                NAME_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentName }));
                });
            }
            previousName.current = currentName;
        }

        if (currentDescription !== previousDescription.current) {
            if (!descriptionTranslationsModified.current) {
                DESCRIPTION_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentDescription }));
                });
            }
            previousDescription.current = currentDescription;
        }

        if (currentUserInfo !== previousUserInfo.current) {
            if (!userInfoTranslationsModified.current) {
                USER_INFO_TRANSLATION_FIELDS.forEach((field) => {
                    dispatch(updateField({ formId: FORM_ID, key: field, value: currentUserInfo }));
                });
            }
            previousUserInfo.current = currentUserInfo;
        }
    }, [formValues, dispatch]);

    const addCategory = () => {
        setValues([...values, '']);
    };

    const removeCategory = (index: number) => {
        setValues((prev) => prev.filter((_, i) => i !== index));
    };

    const updateCategory = (index: number, value: string | null) => {
        setValues((prev) => [...prev.slice(0, index), value ?? '', ...prev.slice(index + 1)]);
    };

    const onSubmit = async (v: CreateProduct): Promise<IProduct> => {
        const product = await ProductClient.postProduct(currentAccount, v);
        const filteredValues = values.filter((val) => val && val !== '');
        if (filteredValues.length > 0) {
            const categories = filteredValues.map((val) =>
                ProductCategoryClient.postProductCategoryMember(currentAccount, val, { entity_id: product.id, entity_type: EntityType.PRODUCT })
            );
            await Promise.all(categories);
        }
        return product;
    };

    const submitDisabled = (v: { [key: string]: any }): boolean => {
        return !v.name || v.name === '' || !v.type;
    };

    return {
        values,
        addCategory,
        removeCategory,
        updateCategory,
        onSubmit,
        submitDisabled
    };
};
